import { useEffect, useState, ChangeEvent } from 'react';
import Cookies from 'universal-cookie';
import InputField from './InputField';
import Card, { CollasableCard } from './Cards';
import { useTranslation } from 'react-i18next';

const cookies = new Cookies();
const specificHeatBarley = 0.41;
const infusionTemp = 100;

interface InfusionState {
  grainWeight: number;
  grainTemp: number;
  waterGrainRatio: number;
  restTemp1: number;
  waterVolume1: string;
  waterTemp1: number;
  restTemp2: number;
  waterVolume2: string;
  restTemp3: number;
  waterVolume3: string;
  restTemp4: number;
  waterVolume4: string;
  restTemp5: number;
  waterVolume5: string;
  [key: string]: number | string;
}

function calculateInfusion(
  grainWeight: number, grainTemp: number, waterGrainRatio: number,
  restTemp1: number, restTemp2: number, restTemp3: number, restTemp4: number, restTemp5: number
): InfusionState {
  const waterTemp1 = parseFloat(((specificHeatBarley / waterGrainRatio) * (restTemp1 - grainTemp) + restTemp1).toFixed(1));
  const waterVolume1 = (waterGrainRatio * grainWeight).toFixed(1);
  let totalVolume = parseFloat(waterVolume1);

  const stepTemps = [restTemp2, restTemp3, restTemp4, restTemp5];
  const prevTemps = [restTemp1, restTemp2, restTemp3, restTemp4];
  const waterVolumes: string[] = ['-', '-', '-', '-'];

  for (let i = 0; i < stepTemps.length; i++) {
    const stepTemp = stepTemps[i];
    const lastStepTemp = prevTemps[i];
    if (stepTemp < infusionTemp && stepTemp > lastStepTemp) {
      const addBoiling = ((stepTemp - lastStepTemp) * ((specificHeatBarley * grainWeight) + totalVolume)) / (infusionTemp - stepTemp);
      totalVolume += addBoiling;
      waterVolumes[i] = addBoiling.toFixed(1);
    } else break;
  }

  return {
    grainWeight, grainTemp, waterGrainRatio,
    restTemp1, waterVolume1, waterTemp1,
    restTemp2, waterVolume2: waterVolumes[0],
    restTemp3, waterVolume3: waterVolumes[1],
    restTemp4, waterVolume4: waterVolumes[2],
    restTemp5, waterVolume5: waterVolumes[3],
  };
}

export default function Infusion() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('infusion.pageTitle');
  }, [t]);

  const [state, setState] = useState<InfusionState>(() =>
    calculateInfusion(
      cookies.get('infusion.grainWeight') || 4.5,
      cookies.get('infusion.grainTemp') || 18,
      cookies.get('infusion.waterGrainRatio') || 2.5,
      cookies.get('infusion.restTemp1') || 68.0,
      cookies.get('infusion.restTemp2') || 0,
      cookies.get('infusion.restTemp3') || 0,
      cookies.get('infusion.restTemp4') || 0,
      cookies.get('infusion.restTemp5') || 0,
    )
  );

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    cookies.set(`infusion.${name}`, value);
    const s = { ...state, [name]: Number(value) };
    setState(calculateInfusion(s.grainWeight as number, s.grainTemp as number, s.waterGrainRatio as number, s.restTemp1 as number, s.restTemp2 as number, s.restTemp3 as number, s.restTemp4 as number, s.restTemp5 as number));
  }

  return (
    <div>
      <CollasableCard title={t('infusion.infoTitle')}>
        <p className="text-muted-foreground text-sm mb-2">
          {t('infusion.infoLine1')}
        </p>
        <p className="text-muted-foreground text-sm">
          {t('infusion.infoLine2')}
        </p>
      </CollasableCard>

      <div className="max-w-2xl">
        <Card title={t('infusion.cardTitle')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <InputField name="grainWeight" label={t('infusion.grainWeight')} step="0.1" value={state.grainWeight} unit="kg" onChange={handleOnChange} />
              <InputField name="grainTemp" label={t('infusion.grainTemp')} step="0.1" value={state.grainTemp} unit="°C" onChange={handleOnChange} />
              <InputField name="waterGrainRatio" label={t('infusion.waterGrainRatio')} step="0.1" value={state.waterGrainRatio} unit="l/kg" onChange={handleOnChange} />
              <InputField name="restTemp1" label={t('infusion.firstRestTemp')} step="0.1" value={state.restTemp1} unit="°C" onChange={handleOnChange} />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-border rounded-md overflow-hidden">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">{t('infusion.tableStep')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('infusion.tableTargetTemp')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('infusion.tableWaterNeeded')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border">
                  <td className="px-3 py-2">1.</td>
                  <td className="px-3 py-2">{state.restTemp1}</td>
                  <td className="px-3 py-2">{state.waterVolume1} l @ {state.waterTemp1} °C</td>
                </tr>
                {[2, 3, 4, 5].map(i => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2">{i}.</td>
                    <td className="px-3 py-2 w-32">
                      <InputField name={`restTemp${i}`} step="0.1" value={state[`restTemp${i}`] as number} onChange={handleOnChange} />
                    </td>
                    <td className="px-3 py-2">{state[`waterVolume${i}`]} l @ 100 °C</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
